import { CellAlign, CellType, HeadCell } from '@dfl/mui-admin-layout';
import { IOrder } from '../interfaces/IOrder';
import { ORDER_PERMISSIONS, SUBORDER_PERMISSIONS } from './order-permissions';
import { OrderShippingCell } from '../components/OrderShippingCell';
import { OrderPaymentGateway } from '../components/OrderPaymentGateway';
import { OrderInvoiceTotalCell } from '../components/OrderInvoiceTotalCell';
import { paidOrderCodeColumn } from 'modules/sales/paid-order/constants';
import {
  subOrderCodeColumn,
  subOrderDistributionCenterColumn,
} from 'modules/sales/sub-orders/constants/sub-order.columns';
import { OrderPaymentMethod } from '../components/OrderPaymentMethod';
import SubOrderStatusCell from '../components/SubOrderStatusCell/SubOrderStatusCell';
import { preOrderCodeColumn } from 'modules/sales/pre-order/constants';
import { IOrderStatus } from 'modules/sales/settings/order-status/interfaces';
import { OrderStatusPicker } from '../components/OrderStatusPicker';
import { OrderRowActions } from '../components/OrderRowActions';
import { SuborderRowActions } from 'modules/sales/sub-orders/components/SuborderRowActions';
import { OnDemandDateCell } from 'modules/common/components/OnDemandDateCell';
import { DeliveryDateCell } from 'modules/common/components/DeliveryDateCell';
import { AvatarNameCell } from 'modules/common/components/AvatarNameCell';
import { FlexBox } from '@dfl/mui-react-common';
import { FmdGoodOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';

export const orderLocationColumn: HeadCell<IOrder> = {
  field: 'shipping.address',
  headerName: 'order:shipping.address.location',
  permissions: [ORDER_PERMISSIONS.VIEW_PAYMENT_INFO],
  renderCell: (shipping: any) => {
    if (!shipping) return null
    return (
      <FlexBox alignItems={'center'} gap={0.5}>
        <FmdGoodOutlined fontSize='small' />
        <Typography variant="body2" fontStyle={'italic'}>
          {[shipping?.cityName, shipping?.stateName].filter(Boolean).join(', ')}
        </Typography>
      </FlexBox>
    )
  },
};

export const orderShippingColumn: HeadCell<IOrder> = {
  field: 'shipping.timeType',
  headerName: 'order:shipping.deliveryTimeType.title',
  permissions: [ORDER_PERMISSIONS.ORDER_VIEW, ORDER_PERMISSIONS.ORDER_STATUS_VIEW],
  atLessOne: false,
  component: OrderShippingCell,
};

export const orderCarrierColumn: HeadCell<IOrder> = {
  field: 'carrier',
  headerName: 'common:provider.CARRIER',
  renderCell: (carrier: any) => (
    <AvatarNameCell
      name={carrier?.carrier?.name}
      hideImage
      link={`/inventory/provider/carriers/${carrier?.carrier?._id as string}/general`}
      secondary={carrier?.driver.name}
      secondarySx={{ fontWeight: 500 }}
    />
  ),
};

export const orderTotalProductColumns: HeadCell<IOrder> = {
  headerName: 'order:totalProducts',
  field: 'totalProducts',
  type: CellType.NUMBER,
  align: CellAlign.CENTER,
  disablePadding: true
};

export const orderReadOnlyStatusColumn: HeadCell<IOrder> = {
  field: 'status',
  headerName: 'order:status.title',
  disablePadding: false,
  align: CellAlign.CENTER,
  renderCell: (status: IOrderStatus, order: IOrder) => (
    <OrderStatusPicker rowId={order?._id as string} value={status} readOnly />
  ),
  permissions: [ORDER_PERMISSIONS.ORDER_VIEW],
};

export const subOrderStatusColumn = (readOnly?: boolean): HeadCell<IOrder> => ({
  field: 'status',
  headerName: 'order:status.title',
  disablePadding: true,
  align: CellAlign.CENTER,
  headerAlign: CellAlign.CENTER,
  renderCell: (status: IOrderStatus, order: IOrder) => (
    <OrderStatusPicker rowId={order?._id as string} value={status} readOnly={readOnly} />
  ),
  permissions: [SUBORDER_PERMISSIONS.SUBORDER_WRITE],
});

export const orderGatewayColumn: HeadCell<IOrder> = {
  field: 'payment.gateway',
  headerName: 'order:payment.gateway.title',
  disablePadding: true,
  align: CellAlign.CENTER,
  component: OrderPaymentGateway,
  permissions: [ORDER_PERMISSIONS.VIEW_PAYMENT_INFO],
};

export const orderPaymentMethodColumn: HeadCell<IOrder> = {
  field: 'payment.paymentMethod',
  headerName: 'order:payment.method.title',
  disablePadding: true,
  align: CellAlign.CENTER,
  component: OrderPaymentMethod,
  permissions: [ORDER_PERMISSIONS.VIEW_PAYMENT_INFO],
};

export const orderPaymentDateColumn: HeadCell<IOrder> = {
  headerName: 'order:billing.paymentDate',
  field: 'payment.paidAt',
  type: CellType.DATE,
  align: CellAlign.CENTER,
  format: 'dd/MM/yyyy | hh:mm',
  permissions: [ORDER_PERMISSIONS.VIEW_PAYMENT_INFO],
};

export const orderInvoiceTotal: HeadCell<IOrder> = {
  field: 'invoice.total',
  headerName: 'order:invoice.total',
  component: OrderInvoiceTotalCell,
  permissions: [ORDER_PERMISSIONS.VIEW_PAYMENT_INFO],
};

export const orderDeliveryDueDateColumn: HeadCell<IOrder> = {
  field: 'shipping.deliveryDueDate',
  headerName: 'order:shipping.deliveryDueDate',
  type: CellType.DATE,
  format: 'dd/MM/yyyy | hh:mm aa',
  width: 250,
  permissions: [ORDER_PERMISSIONS.ORDER_VIEW],
};

export const orderCreateAtColumn: HeadCell<IOrder> = {
  headerName: 'common:createdAt',
  field: 'createdAt',
  type: CellType.DATE,
  width: 200,
  format: 'dd/MM/yyyy | hh:mm aa',
  permissions: [ORDER_PERMISSIONS.VIEW_PAYMENT_INFO],
};

export const orderOnDemandDateColumn: HeadCell<IOrder> = {
  headerName: 'order:shipping.onDemandDate',
  field: 'shipping',
  component: OnDemandDateCell,
  width: 250,
};

export const orderDeliveryDateColumn: HeadCell<IOrder> = {
  headerName: 'order:shipping.deliveredAt',
  field: 'shipping',
  component: DeliveryDateCell,
  width: 250,
  permissions: [ORDER_PERMISSIONS.ORDER_VIEW],
};

export const orderSubOrderColumn: HeadCell<IOrder> = {
  headerName: 'subOrder:list',
  field: 'suborders',
  align: CellAlign.CENTER,
  component: SubOrderStatusCell,
};

export const orderActionsColumn: HeadCell<IOrder> = {
  field: 'actions',
  sortable: false,
  width: 100,
  permissions: [ORDER_PERMISSIONS.ORDER_WRITE],
  headerName: 'common:actions',
  disablePadding: true,
  component: OrderRowActions,
};

export const suborderActionsColumn: HeadCell<IOrder> = {
  field: 'actions',
  sortable: false,
  width: 100,
  permissions: [SUBORDER_PERMISSIONS.SUBORDER_WRITE],
  headerName: 'common:actions',
  disablePadding: true,
  component: SuborderRowActions,
};

export const paidOrderColumns: Array<HeadCell<any>> = [
  paidOrderCodeColumn,
  orderLocationColumn,
  orderReadOnlyStatusColumn,
  orderTotalProductColumns,
  orderInvoiceTotal,
  orderGatewayColumn,
  orderPaymentMethodColumn,
  orderSubOrderColumn,
  orderPaymentDateColumn,
  orderActionsColumn,
];

export const preOrderColumns: Array<HeadCell<any>> = [
  preOrderCodeColumn,
  orderLocationColumn,
  orderReadOnlyStatusColumn,
  orderTotalProductColumns,
  orderInvoiceTotal,
  orderGatewayColumn,
  orderPaymentMethodColumn,
  orderCreateAtColumn,
];

export const subOrderColumns: Array<HeadCell<any>> = [
  subOrderCodeColumn(),
  subOrderDistributionCenterColumn,
  orderLocationColumn,
  subOrderStatusColumn(),
  // orderCarrierColumn,
  orderShippingColumn,
  orderTotalProductColumns,
  orderCreateAtColumn,
  orderOnDemandDateColumn,
  orderDeliveryDueDateColumn,
  orderDeliveryDateColumn,
  suborderActionsColumn,
];
